import type { PropsWithChildren, ReactElement } from 'react';

import classNames from 'classnames';

import type { SortOrder } from '@iconik-io/shared-lib/src/utils/sort/types';
import { Icon, type IconName } from '@iconik-io/uad';

import { ORDER_ASC, ORDER_DESC } from '../../../constants/data';
import type { Column } from '../types';

import mixins from '../../../styles/core/mixins.module.css';
import styles from '../table.module.css';

type Props<T> = PropsWithChildren<{
  renderColumns: Column<T>[];
  sorting?: { [name: string]: SortOrder };
  onSort?: (name: string, order: SortOrder | undefined) => void;
}>;

const Head = <T,>({
  renderColumns,
  sorting,
  onSort,
}: Props<T>): ReactElement => (
  <thead>
    <tr>
      {renderColumns.map(
        (
          { name, type, label, hideLabel, style, sortable, originFieldName },
          index,
        ) => {
          const sortByName = originFieldName ?? name;
          let iconName: IconName = 'sort';
          if (sortable && sorting) {
            if (sorting[sortByName] === ORDER_ASC) {
              iconName = 'arrow_small_n';
            } else if (sorting[sortByName] === ORDER_DESC) {
              iconName = 'arrow_small_s';
            }
          }
          const headingLabel = !hideLabel ? label || name : '';
          const content = sortable ? (
            <span
              className={styles.sort_header}
              onClick={(): void => {
                if (onSort && sorting) {
                  onSort(sortByName, sorting[sortByName]);
                }
              }}
            >
              {headingLabel}
              <Icon
                name={iconName}
                size="xs"
                className={classNames(styles.sort_icon, mixins.text_align_icon)}
              />
            </span>
          ) : (
            headingLabel
          );
          const labelIcon = name.startsWith('metadata.') && (
            <Icon
              name="Svg2022_uad_metadata"
              className={classNames(styles.label_icon, mixins.text_align_icon)}
            />
          );
          return (
            <th
              key={`${name}-${index}`}
              className={classNames(styles.th, {
                [styles.title]: type === 'title',
              })}
              style={style}
            >
              <div className={styles.clipped_shadow} />
              <div className={styles.label_wrapper}>
                <div className={styles.label}>
                  {labelIcon}
                  {content}
                </div>
              </div>
            </th>
          );
        },
      )}
    </tr>
  </thead>
);

export default Head;
